import React, { memo, useEffect } from 'react'
import { Keyboard } from 'react-native'
import { useDispatch } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useForm } from 'react-hook-form'
import { PublicComputerType } from 'APP/Lib/Enums'
import { useDevTools } from 'APP/Lib/DevTools'
import I18n from 'APP/I18n'
import LoginActions from 'APP/Redux/LoginRedux'
import ControllerRadioGroup from 'APP/Components/ControllerFields/ControllerRadioGroup'
import Card from 'APP/Converse/Card'
import Button from 'APP/Converse/Button'
import ControllerCheckbox from 'APP/Components/ControllerFields/ControllerCheckbox'
import LogoHeader from 'APP/Components/LogoHeader'
import { Images } from 'APP/Themes'

const Container = styled(SafeAreaView)`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  background-color: ${({ theme }) => theme.colors.background};
`

const ContentCard = styled(Card)`
  max-width: 550px;
  width: 100%;
  align-items: start;
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const ContentHeading = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

interface FormProps {
  publicComputer: PublicComputerType | null
  dontAskPublicComputer: boolean
}

const options = [
  {
    label: I18n.t('PublicComputerScreen.yes'),
    value: PublicComputerType.ANSWERED_YES,
  },
  {
    label: I18n.t('PublicComputerScreen.no'),
    value: PublicComputerType.ANSWERED_NO,
  },
]

const PublicDeviceScreen = () => {
  const dispatch = useDispatch()
  const { setToolFunction, setToolName } = useDevTools()

  useEffect(() => {
    setToolName('Private')
    setToolFunction(() => () => {
      dispatch(
        LoginActions.submitPublicComputer({ publicComputer: PublicComputerType.ANSWERED_NO })
      )
    })
    return () => {
      setToolFunction(null)
      setToolName(null)
    }
  }, [dispatch, setToolName, setToolFunction])

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      publicComputer: null,
      dontAskPublicComputer: false,
    },
  })

  const publicComputer = watch('publicComputer')

  const onSubmit = (values: FormProps) => {
    dispatch(LoginActions.submitPublicComputer(values))
  }

  return (
    <Container>
      <ContentCard testID="PUBLIC_COMPUTER_CONTENT_CARD">
        <ContentHeading>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('PublicComputerScreen.title')}
            titleVariant="h3"
            subtitle={I18n.t('PublicComputerScreen.subtitle')}
          />

          <ControllerRadioGroup
            testID="PUBLIC_PC_RADIO_GROUP"
            name="publicComputer"
            control={control}
            options={options}
          />
        </ContentHeading>

        <ControllerCheckbox
          name="dontAskPublicComputer"
          control={control}
          label={I18n.t('PublicComputerScreen.remember')}
        />

        <Button
          variant="primary"
          widthVariant="full"
          onPress={() => {
            Keyboard.dismiss()
            handleSubmit(onSubmit)()
          }}
          analyticsName="Next"
          disabled={!publicComputer}
          title={I18n.t('PublicComputerScreen.continue')}
        />
      </ContentCard>
    </Container>
  )
}

export default memo(PublicDeviceScreen)
